import {
  ADDONS,
  LANGUAGES,
  SHIPPING_OPTIONS
} from '../../constants/storefrontOrderConstants';
import { buildGeneratedSku } from './sku';
import { splitCustomerName } from './validation';

// Add-on order-item names, byte-identical to the storefront (order-form.liquid
// lines 5096-5145) including emoji — backend and Shopify automations may match
// on them (spec R2). Premium printing is not an add-on: it swaps the story
// line to the story's premium variant (spec addendum 8.2).
const ADDON_ITEM_NAMES = {
  [ADDONS.giftBox.sku]: 'Gift Wrapping ( Gift Box 🎁 )',
  [ADDONS.giftNote.sku]: 'Gift Note ( Write your Message ) ✉️'
};

/**
 * Ticked add-ons for one entry, in the fixed storefront order: gift box,
 * gift note. Premium printing is excluded — it swaps the story line to the
 * premium variant instead of emitting an add-on line (spec addendum 8.2).
 * The single source of ordering shared by buildQuoteRequest and
 * buildOrderPayload so quote lines and order lines can never diverge.
 *
 * @param {Object} entry
 * @returns {Object[]} The ADDONS entries in tick order.
 */
function getTickedAddons(entry) {
  const ticked = [];
  if (entry.giftBox) {
    ticked.push(ADDONS.giftBox);
  }
  if (entry.giftNote) {
    ticked.push(ADDONS.giftNote);
  }
  return ticked;
}

/**
 * Resolve the premium upgrade for an entry: when premium printing is ticked
 * and the selected variant carries premium data, return that premium
 * variant descriptor; otherwise null (the story line stays standard).
 *
 * @param {Object} entry
 * @returns {Object|null} { variant_id, sku, price } or null.
 */
function resolvePremium(entry) {
  return entry.premiumPrinting && entry.variant && entry.variant.premium
    ? entry.variant.premium
    : null;
}

/**
 * Build the add-on order lines for one entry (storefront lines 5096-5145):
 * names byte-identical to the storefront including emoji, variant_id null,
 * fallback display price, and `_Related Story` pointing at the story line's
 * name. Only gift box and gift note — premium printing never emits an
 * add-on line (spec addendum 8.2).
 *
 * @param {Object} entry - The entry with giftBox/giftNote flags.
 * @param {string} storyItemName - The story line's `name` field, or falsy
 *   to fall back to 'Story'.
 * @returns {Object[]} One line per ticked add-on, in getTickedAddons order.
 */
function buildAddonItems(entry, storyItemName) {
  const relatedStory = storyItemName || 'Story';

  return getTickedAddons(entry).map(addon => {
    return {
      name: ADDON_ITEM_NAMES[addon.sku],
      sku: addon.sku,
      variant_sku: addon.sku,
      generated_sku: addon.sku,
      variant_id: null,
      price: addon.fallbackPrice,
      quantity: 1,
      properties: [{ name: '_Related Story', value: relatedStory }],
      photos: []
    };
  });
}

/**
 * Look up a shipping option by its form value ('regular' | 'express').
 *
 * @param {string} shippingValue
 * @returns {Object} The matching SHIPPING_OPTIONS entry.
 */
function findShippingOption(shippingValue) {
  const option = SHIPPING_OPTIONS.find(item => item.value === shippingValue);
  return option || SHIPPING_OPTIONS[0];
}

/**
 * Look up a language label from its ISO code.
 *
 * @param {string} languageCode
 * @returns {string} The label, or the raw code when unknown.
 */
function languageLabel(languageCode) {
  const language = LANGUAGES.find(item => item.code === languageCode);
  return language ? language.label : languageCode;
}

/**
 * Build the request body for the calculate-shopify-cart quote endpoint.
 * Mirrors the storefront's buildShopifyPricingPayload two-loop item order
 * (spec 3.7): loop 1 one item per story, loop 2 the ticked add-on SKUs per
 * story in story order. Discount_code only when non-empty after trimming.
 *
 * @param {Object} params
 * @param {Object[]} params.entries - Entry list.
 * @param {string} params.shippingValue - 'regular' | 'express'.
 * @param {string} [params.discountCode] - Raw discount code input.
 * @returns {{ items: ({ variant_id: number, quantity: number }|{ sku: string, quantity: number })[], shipping_code: string, discount_code?: string }}
 */
export function buildQuoteRequest({ entries, shippingValue, discountCode }) {
  const storyEntries = (entries || []).filter(
    entry => entry.story && entry.variant
  );

  // Loop 1: one story variant per story, in story order. Premium-ticked
  // entries send the story's premium variant id (spec addendum 8.2).
  const items = storyEntries.map(entry => ({
    variant_id: Number(
      (entry.premiumPrinting && entry.variant.premium
        ? entry.variant.premium.variant_id
        : entry.variant.id)
    ),
    quantity: 1
  }));

  // Loop 2: per story, in story order, the ticked add-on SKUs.
  storyEntries.forEach(entry => {
    getTickedAddons(entry).forEach(addon => {
      items.push({ sku: addon.sku, quantity: 1 });
    });
  });

  const request = {
    items,
    shipping_code: findShippingOption(shippingValue).code
  };

  const trimmedCode = (discountCode || '').trim();
  if (trimmedCode) {
    request.discount_code = trimmedCode;
  }

  return request;
}

/**
 * Stable fingerprint for a quote request, used to cache and reuse quotes
 * when the form has not changed. Key order does not matter.
 *
 * @param {Object} request - A buildQuoteRequest result.
 * @returns {string}
 */
export function fingerprintQuoteRequest(request) {
  return JSON.stringify(request, (_key, value) =>
    value !== null && typeof value === 'object' && !Array.isArray(value)
      ? Object.keys(value)
          .sort()
          .reduce((sorted, key) => {
            sorted[key] = value[key];
            return sorted;
          }, {})
      : value
  );
}

/**
 * Build the payload for POST /orders/ (the storefront order path),
 * mirroring the Shopify storefront form exactly (spec section 7).
 *
 * @param {Object} params
 * @param {Object[]} params.entries - Entry list.
 * @param {Object} params.customer - { name, phone, email, address, city }.
 * @param {string} params.shippingValue - 'regular' | 'express'.
 * @param {string} params.paymentMethod - Payment method value.
 * @param {string} [params.note] - Optional internal note.
 * @param {Object} params.quote - Quote from calculate-shopify-cart.
 * @param {string} params.userEmail - Dashboard user email for the order note.
 * @returns {Object}
 */
export function buildOrderPayload({
  entries,
  customer,
  shippingValue,
  paymentMethod,
  note,
  quote,
  userEmail
}) {
  const shippingOption = findShippingOption(shippingValue);
  const { firstName, lastName } = splitCustomerName(customer.name);

  const address = {
    first_name: firstName,
    last_name: lastName,
    address1: customer.address || '',
    address2: '',
    city: customer.city,
    province: customer.city,
    country: 'EG',
    phone: customer.phone || '',
    zip: '12345'
  };

  const items = buildOrderItems(entries);

  const orderNote = `Created from Snaps Dashboard by ${userEmail || ''}`;
  const trimmedNote = (note || '').trim();

  const payload = {
    source: 'api',
    customer: {
      first_name: firstName,
      last_name: lastName,
      phone: customer.phone || '',
      email: customer.email || ''
    },
    shipping_address: address,
    billing_address: { ...address },
    shipping_lines: [
      {
        title: shippingOption.title,
        code: shippingOption.code,
        price: shippingOption.price.toFixed(2)
      }
    ],
    items,
    payment: {
      method: paymentMethod,
      status: 'pending',
      total_amount: quote.total,
      currency: 'EGP'
    },
    shopify_pricing: true,
    shopifyOptions: {
      // Never add the 'paid' tag here; payment status is managed elsewhere.
      tags: ['api-order', 'dashboard-order', shippingOption.code],
      note: trimmedNote ? `${orderNote}\n${trimmedNote}` : orderNote,
      sendReceipt: true
    },
    pushToShopify: true
  };

  if (quote.discount_code && quote.discount_code.applied) {
    payload.discount_code = quote.discount_code.code;
  }

  return payload;
}

/**
 * Build the order `items` array: story lines first in entry order, then per
 * entry the ticked add-on lines.
 *
 * @param {Object[]} entries
 * @returns {Object[]}
 */
export function buildOrderItems(entries) {
  const storyItems = (entries || []).map(entry => {
    const { story, variant } = entry;
    const photos = (entry.photos || []).filter(photo => photo.status === 'done');
    const hasGiftBox = Boolean(entry.giftBox);
    const hasGiftNote = Boolean(entry.giftNote);
    const generatedSku = buildGeneratedSku({
      handle: story.handle,
      age: story.age,
      pagesType: variant.pages_type,
      languageCode: entry.languageCode,
      hasGiftNote,
      hasGiftBox
    });

    // Premium printing swaps the story line itself to the story's premium
    // variant (spec addendum 8.2); the generated SKU is unchanged.
    const premium = resolvePremium(entry);

    const properties = [
      { name: 'Story Language', value: languageLabel(entry.languageCode) },
      { name: 'Kid Name', value: entry.kidName },
      { name: 'SKU', value: generatedSku },
      { name: 'Gift Box', value: hasGiftBox ? 'Yes' : 'No' },
      { name: 'Gift Note', value: hasGiftNote ? 'Yes' : 'No' },
      // Always present, mirroring the storefront (even when empty).
      { name: 'Gift Message', value: entry.giftMessage || '' }
    ];

    if (premium) {
      properties.push(
        { name: 'Premium Printing', value: 'Hard Cover + Premium Papers' },
        { name: '_PremiumPrintingGroup', value: `premium-${entry.key}` },
        { name: '_PrintingProfile', value: 'premium_hardcover_v1' }
      );
    }

    return {
      name: `${story.title} - ${variant.title}`,
      sku: story.handle,
      variant_sku: premium ? premium.sku : variant.sku,
      generated_sku: generatedSku,
      variant_id: String(premium ? premium.variant_id : variant.id),
      // Same convention as the standard story line (numeric display price);
      // the backend re-prices with shopify_pricing anyway.
      price: Number(premium ? premium.price : variant.price),
      quantity: 1,
      kid_name: entry.kidName,
      kid_image_url: photos.length > 0 ? photos[0].url : '',
      photos: photos.map(photo => ({ url: photo.url })),
      properties
    };
  });

  // Add-on lines are appended only after ALL story lines (spec 3.4): story
  // lines first in entry order, then per entry in entry order the ticked
  // add-ons, mirroring the quote's two-loop item order.
  const items = [...storyItems];
  (entries || []).forEach((entry, index) => {
    items.push(...buildAddonItems(entry, storyItems[index].name));
  });

  return items;
}

/**
 * Group quote line items per story entry for the order summary UI. The quote
 * line-item order is deterministic (buildQuoteRequest): all story lines in
 * entry order first, then per entry the ticked add-ons in storefront order.
 * Premium printing never appears here — it swaps the story line's variant
 * (spec addendum 8.2).
 *
 * @param {Object[]} entries - Entry list (incomplete entries are skipped and
 *   consume no line slots, mirroring buildQuoteRequest's filter).
 * @param {Object[]} lineItems - quote.line_items.
 * @returns {Object[]} [{ key, entry, storyLine, addons: [{sku,label,quantity,originalTotal}] }]
 */
export function buildSummaryGroups(entries, lineItems) {
  const items = Array.isArray(lineItems) ? lineItems : [];
  const storyEntries = (entries || []).filter(entry => entry.story && entry.variant);

  const groups = storyEntries.map((entry, index) => ({
    key: entry.key,
    entry,
    storyLine: items[index] || null,
    addons: []
  }));

  let cursor = storyEntries.length;
  storyEntries.forEach((entry, index) => {
    getTickedAddons(entry).forEach(addon => {
      const line = items[cursor];
      cursor += 1;
      if (line) {
        groups[index].addons.push({
          sku: addon.sku,
          label: addon.label,
          quantity: line.quantity,
          originalTotal: line.original_total
        });
      }
    });
  });

  return groups;
}
